import React, { useEffect, useRef, useState } from "react";
import { Field, ErrorMessage } from "formik";
import { Upload, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import TextError from "./TextError";
import api from "../../store/client";

const SHAPE_CONFIG = {
  square: { width: 104, height: 104, radius: "8px" },
  rectangle: { width: 130, height: 80, radius: "8px" },
  circle: { width: 104, height: 104, radius: "50%" },
};

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

function toAbsoluteUrl(url) {
  if (!url || url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) return url;
  const base = (api.defaults.baseURL ?? "").replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
}

// Resizes a File using Canvas if it exceeds maxSizeKb.
// Outputs JPEG (best compression); preserves original if already small enough.
async function resizeImage(
  file,
  { maxSizeKb = 100, resizeMaxWidth = 1280, resizeQuality = 0.8 },
) {
  if (file.size / 1024 <= maxSizeKb) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > resizeMaxWidth) {
        height = Math.round((height * resizeMaxWidth) / width);
        width = resizeMaxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: "image/jpeg" })),
        "image/jpeg",
        resizeQuality,
      );
    };
    img.src = objectUrl;
  });
}

function ImageUploadInner({
  label,
  name,
  uploadUrl = "upload/image",
  maxCount = 1,
  shape = "square",
  deferred = false,
  maxSizeKb = 100,
  resizeMaxWidth = 1280,
  resizeQuality = 0.8,
  field,
  form,
}) {
  const [fileList, setFileList] = useState([]);
  const [preview, setPreview] = useState({ open: false, image: "", title: "" });

  // Track UIDs already handled in beforeUpload to avoid double Formik writes in handleChange
  const processedUids = useRef(new Set());
  // Track blob URLs created for authenticated images so we can revoke them on unmount
  const blobUrls = useRef([]);

  // Sync fileList whenever field.value changes (covers Formik reinitialization after data loads)
  useEffect(() => {
    if (!field.value) {
      setFileList([]);
      return;
    }
    const vals = Array.isArray(field.value) ? field.value : [field.value];

    // File objects are already in fileList via beforeUpload/handleChange — don't overwrite
    if (vals.some((v) => v instanceof File)) return;

    setFileList(
      vals
        .filter((v) => typeof v === "string" && v)
        .map((v, i) => ({
          uid: String(-i - 1),
          name: `image-${i + 1}`,
          status: "done",
          url: null,           // blank until auth fetch resolves
          _apiUrl: toAbsoluteUrl(v),
        })),
    );
  }, [field.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever fileList gains _apiUrl entries, fetch them with the auth token
  useEffect(() => {
    const pending = fileList.filter((f) => f._apiUrl);
    if (!pending.length) return;

    let active = true;
    (async () => {
      const updates = await Promise.all(
        pending.map(async (f) => {
          try {
            const res = await api.get(f._apiUrl, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(res.data);
            blobUrls.current.push(blobUrl);
            return { uid: f.uid, url: blobUrl };
          } catch {
            return { uid: f.uid, url: "" };
          }
        }),
      );
      if (!active) return;
      setFileList((prev) =>
        prev.map((f) => {
          const update = updates.find((u) => u.uid === f.uid);
          if (!update) return f;
          const { _apiUrl: _, ...rest } = f;
          return { ...rest, url: update.url };
        }),
      );
    })();

    return () => { active = false; };
  }, [fileList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke blob URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => { blobUrls.current.forEach(URL.revokeObjectURL); };
  }, []);

  const { width, height, radius } = SHAPE_CONFIG[shape] ?? SHAPE_CONFIG.square;
  const scopeClass = `img-upload-${name.replace(/[^a-zA-Z0-9]/g, "-")}`;
  const resizeOpts = { maxSizeKb, resizeMaxWidth, resizeQuality };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreview({
      open: true,
      image: file.url || file.preview,
      title: file.name || file.url?.substring(file.url.lastIndexOf("/") + 1),
    });
  };

  // Deferred mode: resize here, store File in Formik, block upload
  const beforeUpload = deferred
    ? async (file) => {
        const prepared = await resizeImage(file, resizeOpts);
        processedUids.current.add(file.uid);
        if (maxCount === 1) {
          form.setFieldValue(name, prepared);
        } else {
          const current = Array.isArray(field.value) ? field.value : [];
          form.setFieldValue(name, [...current, prepared]);
        }
        return false;
      }
    : undefined;

  const handleChange = ({ file, fileList: newList }) => {
    // Deferred: Formik already updated in beforeUpload; just sync display list
    if (deferred && processedUids.current.has(file.uid)) {
      setFileList(newList.map((f) => ({ ...f, status: f.status ?? "done" })));
      return;
    }
    setFileList(newList);
  };

  // Immediate mode: resize then post to API
  const customRequest = deferred
    ? undefined
    : async (options) => {
        const prepared = await resizeImage(options.file, resizeOpts);
        const data = new FormData();
        data.append("file", prepared);
        try {
          const response = await api.post(uploadUrl, data, {
            headers: { "content-type": "multipart/form-data" },
          });
          options.onSuccess(response.data, options.file);
          const url = response.data?.url ?? response.data;
          if (maxCount === 1) {
            form.setFieldValue(name, url);
          } else {
            const current = Array.isArray(field.value) ? field.value : [];
            form.setFieldValue(name, [...current, url]);
          }
        } catch (err) {
          options.onError(err);
        }
      };

  const handleRemove = (file) => {
    processedUids.current.delete(file.uid);
    if (maxCount === 1) {
      form.setFieldValue(name, "");
    } else {
      const current = Array.isArray(field.value) ? field.value : [];
      form.setFieldValue(
        name,
        current.filter((v) =>
          v instanceof File ? v !== file.originFileObj : v !== file.url,
        ),
      );
    }
  };

  return (
    <div>
      <div className="mb-1">
        <label htmlFor={name}>{label}</label>
      </div>
      <div className="mb-1">
        <div className={scopeClass}>
          <style>{`
            .${scopeClass} .ant-upload-select,
            .${scopeClass} .ant-upload-list-item-container {
              width: ${width}px !important;
              height: ${height}px !important;
            }
            .${scopeClass} .ant-upload-select,
            .${scopeClass} .ant-upload-list-item {
              border-radius: ${radius} !important;
              overflow: hidden;
            }
            .${scopeClass} .ant-upload-list-item-thumbnail,
            .${scopeClass} .ant-upload-list-item-thumbnail img {
              width: 100% !important;
              height: 100% !important;
              border-radius: ${radius} !important;
              object-fit: cover !important;
            }
          `}</style>
          <Upload
            listType="picture-card"
            fileList={fileList}
            maxCount={maxCount}
            accept="image/*"
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            onPreview={handlePreview}
            onChange={handleChange}
            onRemove={handleRemove}
          >
            {fileList.length >= maxCount ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>
        </div>
        <Modal
          open={preview.open}
          title={preview.title}
          footer={null}
          onCancel={() => setPreview((p) => ({ ...p, open: false }))}
        >
          <img alt="preview" style={{ width: "100%" }} src={preview.image} />
        </Modal>
      </div>
      <ErrorMessage component={TextError} name={name} />
    </div>
  );
}

function ImageUpload(props) {
  const {
    label,
    name,
    uploadUrl,
    maxCount,
    shape,
    deferred,
    maxSizeKb,
    resizeMaxWidth,
    resizeQuality,
  } = props;
  return (
    <Field name={name}>
      {({ field, form }) => (
        <ImageUploadInner
          label={label}
          name={name}
          uploadUrl={uploadUrl}
          maxCount={maxCount}
          shape={shape}
          deferred={deferred}
          maxSizeKb={maxSizeKb}
          resizeMaxWidth={resizeMaxWidth}
          resizeQuality={resizeQuality}
          field={field}
          form={form}
        />
      )}
    </Field>
  );
}

export default ImageUpload;
